/*
 *   Copyright (c) 2022 Busy Human LLC
 *   All rights reserved.
 *   This file, its contents, concepts, methods, behavior, and operation  (collectively the "Software") are protected by trade secret, patent,  and copyright laws. The use of the Software is governed by a license  agreement. Disclosure of the Software to third parties, in any form,  in whole or in part, is expressly prohibited except as authorized by the license agreement.
 */
import { CollectionEntity } from "@busy-human/gearbox";
export var NotificationTokens;
(function (NotificationTokens) {
    function Defaults() {
        var now = (new Date()).toISOString();
        return {
            token: "",
            companyId: "",
            userType: null,
            userId: "",
            unitId: null,
            lastUpdated: now,
            lastUsed: now,
            ua: null,
            deviceId: ""
        };
    }
    NotificationTokens.Defaults = Defaults;
    function lastestTokensByUser(tokens) {
        const users = Array.from(new Set(tokens.map(t => t.userId)));
        const selectedTokens = [];
        for (const user of users) {
            // Sort them by last used for simplification
            const userTokens = tokens.filter(t => t.userId === user).sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime());
            const hasDeviceId = userTokens.some(u => !!u.deviceId);
            if (hasDeviceId) {
                // New behaviour, we just push to all of them
                selectedTokens.push(...userTokens.filter(u => !!u.deviceId));
            }
            else {
                // Old behaviour, only push to the latest
                selectedTokens.push(userTokens[0]);
            }
        }
        return selectedTokens;
    }
    class CollectionClass extends CollectionEntity {
        relationships;
        defaults() {
            return Defaults();
        }
        async coerceDocForUser(userId, data) {
            let doc;
            try {
                const tmp = await this.subset((ref, query) => query.where('userId', '==', userId).toQuery()).fetch();
                if (tmp.length > 1) {
                    await this.deleteTokensForUser(userId);
                }
                else if (tmp.length > 0) {
                    doc = tmp[0];
                }
            }
            catch (err) {
                console.warn(err);
            }
            data.userId = userId;
            if (!doc) {
                return (await this.add(data)).data();
            }
            else {
                await doc.update(data);
                return doc.data();
            }
        }
        async coerceDocForDevice(deviceId, data) {
            let doc;
            try {
                const foundDocs = await this.fetchTokensForDevice(deviceId);
                if (foundDocs.length > 1) {
                    await this.deleteTokensForDevice(deviceId);
                }
                else if (foundDocs.length > 0) {
                    doc = foundDocs[0];
                }
            }
            catch (e) {
                console.error(e);
            }
            if (!doc) {
                return (await this.add(data)).data();
            }
            else {
                await this.docEntity(doc.$id).update(data);
                return { ...doc, ...data };
            }
        }
        async fetchSubsetForUnit(companyId, unitId) {
            let subset = this.subset((ref, query) => {
                return query.where("companyId", "==", companyId)
                    .where("unitId", "==", unitId).toQuery();
            });
            await subset.fetch();
            return subset;
        }
        /**
         * Returns the latest token per user for the given unit
         * @param companyId
         * @param unitId
         * @returns
         */
        async fetchTokensForUnit(companyId, unitId) {
            let subset = await this.fetchSubsetForUnit(companyId, unitId);
            return lastestTokensByUser(subset.dataItems());
        }
        async fetchTokensForUserType(companyId, userType) {
            let subset = this.subset((ref, query) => {
                return query.where("companyId", "==", companyId).where("userType", "==", userType).toQuery();
            });
            await subset.fetch();
            return lastestTokensByUser(subset.dataItems());
        }
        /**
         * Returns the latest token per user for the given company
         * @param companyId
         * @returns
         */
        async fetchTokensForCompany(companyId) {
            let subset = this.subset((ref, query) => {
                return query.where("companyId", "==", companyId).toQuery();
            });
            await subset.fetch();
            return lastestTokensByUser(subset.dataItems());
        }
        async fetchTokensForUser(userId) {
            let subset = this.subset((ref, query) => query.where("userId", "==", userId).toQuery());
            await subset.fetch();
            return subset.dataItems();
        }
        async fetchTokensForDevice(deviceId) {
            const subset = this.subset((_ref, query) => query.where("deviceId", "==", deviceId).toQuery());
            await subset.fetch();
            return subset.dataItems();
        }
        async updateTokensForUser(userId, updates) {
            let subset = await this.fetchTokensForUser(userId);
            return Promise.all(subset.map(async (t) => {
                return this.docEntity(t.$id).update(updates);
            }));
        }
        async deleteTokensForUser(userId) {
            let subset = await this.fetchTokensForUser(userId);
            for (let item of subset) {
                await this.deleteDoc(item.$id);
            }
        }
        async deleteTokensForDevice(deviceId) {
            const subset = await this.fetchTokensForDevice(deviceId);
            for (const item of subset) {
                try {
                    await this.deleteDoc(item.$id);
                }
                catch (e) {
                    console.error(e);
                }
            }
        }
    }
    NotificationTokens.CollectionClass = CollectionClass;
    NotificationTokens.Collection = new CollectionClass({ path: "/notification-tokens" });
})(NotificationTokens || (NotificationTokens = {}));
//# sourceMappingURL=NotificationTokens.js.map